import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminFeatureIdeasApi } from '../../lib/api';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/**
 * Ideas submitted through the sidebar "Got an idea?" box, ranked by votes
 * (see backend/app/services/feature_ideas.py for how similar submissions
 * get combined). Mark complete when it's shipped; reopen if that was a
 * mistake or it needs more work.
 */
const AdminFeatureIdeas = () => {
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminFeatureIdeasApi
      .list(status)
      .then((res) => !cancelled && setRows(res.data?.results || []))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [status]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await (action === 'complete' ? adminFeatureIdeasApi.complete(id) : adminFeatureIdeasApi.reopen(id));
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success(action === 'complete' ? 'Marked complete.' : 'Reopened.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update that. Try again in a moment.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-feature-ideas">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.03em] text-ink">Feature ideas</h1>
          <p className="text-ink-70 mt-1">
            What people wish this could do, ranked by how many asked for it.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant={status === 'open' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setStatus('open')}
            data-testid="feature-ideas-tab-open"
          >
            Open
          </Button>
          <Button
            variant={status === 'completed' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setStatus('completed')}
            data-testid="feature-ideas-tab-completed"
          >
            Completed
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-ink-55">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-55" data-testid="feature-ideas-empty">
          {status === 'open' ? 'No ideas waiting right now.' : 'Nothing marked complete yet.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Idea</TableHead>
              <TableHead>Votes</TableHead>
              <TableHead>First suggested</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`feature-idea-row-${r.id}`}>
                <TableCell className="max-w-[48ch]">
                  <p className="font-medium text-ink">{r.title}</p>
                  {r.description && r.description !== r.title && (
                    <p className="text-sm text-ink-55 mt-0.5">{r.description}</p>
                  )}
                </TableCell>
                <TableCell className="text-ink font-semibold">{r.votes}</TableCell>
                <TableCell className="text-ink-70">{fmt(r.created_at)}</TableCell>
                <TableCell>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => act(r.id, status === 'open' ? 'complete' : 'reopen')}
                    data-testid={`feature-idea-${status === 'open' ? 'complete' : 'reopen'}-${r.id}`}
                  >
                    {status === 'open' ? 'Mark complete' : 'Reopen'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default AdminFeatureIdeas;
